import { useEffect, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import type { RemoteTrack } from "livekit-client";

import { useStore } from "../store";
import { useLiveKit } from "./LiveKitProvider";
import { useVoiceStore } from "./store";

/**
 * Per-remote-participant VU meter. Returns a ref<number> that is updated
 * each R3F frame to a smoothed amplitude in [0, 1] derived from the
 * participant's live audio track. Use this to drive a mouth animation —
 * read `ref.current` inside useFrame on whatever mesh you're animating.
 *
 * The hook also short-circuits when the listener has locally muted the
 * participant; in that case the level stays at 0 so the mouth stops.
 *
 * Why a ref rather than zustand state: this updates at 60Hz per remote
 * player. A React state path would tear down/rebuild trees on every frame.
 */
export function useSpeakingLevel(identity: string): React.MutableRefObject<number> {
  const levelRef = useRef(0);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const dataRef = useRef<Uint8Array | null>(null);
  const { getRemoteTrack } = useLiveKit();
  const trackBoundRef = useRef<RemoteTrack | null>(null);

  // (Re)build the analyser whenever the underlying track changes.
  useEffect(() => {
    function tryBind(): void {
      const track = getRemoteTrack(identity);
      if (!track) return;
      if (track === trackBoundRef.current) return;
      teardown();

      try {
        const mediaStreamTrack = track.mediaStreamTrack;
        if (!mediaStreamTrack) return;
        // One AudioContext per analyser is fine in modern browsers and
        // avoids cross-contaminating volumes via a shared graph.
        const AnyAudioCtx: typeof AudioContext =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext: typeof AudioContext })
            .webkitAudioContext;
        const ctx = new AnyAudioCtx();
        const source = ctx.createMediaStreamSource(new MediaStream([mediaStreamTrack]));
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.2;
        source.connect(analyser);
        analyserRef.current = analyser;
        ctxRef.current = ctx;
        dataRef.current = new Uint8Array(analyser.fftSize);
        trackBoundRef.current = track;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[voice] analyser bind failed", err);
      }
    }
    function teardown(): void {
      analyserRef.current = null;
      dataRef.current = null;
      trackBoundRef.current = null;
      const ctx = ctxRef.current;
      ctxRef.current = null;
      if (ctx) void ctx.close().catch(() => undefined);
    }

    // Poll for the track for a short window after mount; LiveKit emits
    // TrackSubscribed asynchronously after participant join.
    tryBind();
    const interval = window.setInterval(tryBind, 250);
    return () => {
      window.clearInterval(interval);
      teardown();
    };
  }, [identity, getRemoteTrack]);

  // Drive the level each frame. Cheap: a single RMS pass over 512 samples.
  useFrame(() => {
    const analyser = analyserRef.current;
    const data = dataRef.current;
    if (!analyser || !data) {
      // Decay toward 0 so a stale value doesn't keep the mouth open.
      levelRef.current *= 0.85;
      return;
    }
    // Check listener-side mute: if I muted this person, freeze the mouth so
    // muted players visually go silent too.
    const muted = useVoiceStore.getState().mutedKeys;
    const player = useStore.getState().remotePlayers.get(identity);
    const pkKey = player && player.pubkey ? `pk:${player.pubkey}` : "";
    const sidKey = `sid:${identity}`;
    if (muted.has(sidKey) || (pkKey && muted.has(pkKey))) {
      levelRef.current *= 0.85;
      return;
    }

    analyser.getByteTimeDomainData(data);
    let sumSq = 0;
    for (let i = 0; i < data.length; i++) {
      const v = (data[i]! - 128) / 128;
      sumSq += v * v;
    }
    const rms = Math.sqrt(sumSq / data.length);
    // Map raw RMS [0..~0.4] to mouth [0..1] with a small floor so quiet
    // speech still opens the mouth a bit; clamp on the high end.
    const target = Math.min(1, Math.max(0, (rms - 0.02) * 6));
    // Single-pole low-pass for visual stability (mouth doesn't jitter).
    levelRef.current = levelRef.current * 0.6 + target * 0.4;
  });

  return levelRef;
}

